import { NgModule } from '@angular/core';
import { ExtraOptions, RouterModule, Routes } from '@angular/router';

import { restrictedFeatureGuard, structureFeaturesGuard, unauthenticatedOnlyGuard } from './shared/guards';

const staticRoutes: Routes = [
  // External Pages
  {
    path: 'company-registration',
    canActivate: [unauthenticatedOnlyGuard],
    loadComponent: () =>
      import('./pages-external/company-registration/company-registration.component').then(
        (c) => c.CompanyRegistrationComponent,
      ),
  },
  {
    path: 'company-registration-success',
    canActivate: [unauthenticatedOnlyGuard],
    loadComponent: () =>
      import('./pages-external/company-registration-success/company-registration-success.component').then(
        (c) => c.CompanyRegistrationSuccessComponent,
      ),
  },
  {
    path: 'company-registration-error',
    canActivate: [unauthenticatedOnlyGuard],
    loadComponent: () =>
      import('./pages-external/company-registration-error/company-registration-error.component').then(
        (c) => c.CompanyRegistrationErrorComponent,
      ),
  },
  // Layout pages
  {
    path: '',
    loadComponent: () => import('./layout/app-layout.component').then((c) => c.LayoutComponent),
    children: [
      // Main Pages
      {
        path: 'dashboard',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/main-pages/dashboard/dashboard.component').then((c) => c.DashboardComponent),
      },
      // Legacy prefix (pre-restructure): still used by inSign callback URLs and backend email links,
      // e.g. /clients/detail/:uid/insign?insignsessionid=… → /clients-overview/detail/:uid/insign (query params kept)
      {
        path: 'clients',
        redirectTo: 'clients-overview',
      },
      {
        path: 'clients-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/main-pages/clients-overview/clients-overview.component').then(
            (c) => c.ClientsOverviewComponent,
          ),
        children: [
          {
            path: '',
            redirectTo: 'all-clients',
            pathMatch: 'full',
          },
          {
            path: 'all-clients',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/all-clients-list/all-clients-list.component').then(
                (c) => c.AllClientsListComponent,
              ),
          },
          {
            path: 'active-clients',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/all-clients-list/all-clients-list.component').then(
                (c) => c.AllClientsListComponent,
              ),
          },
          {
            path: 'inactive-clients',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/all-clients-list/all-clients-list.component').then(
                (c) => c.AllClientsListComponent,
              ),
          },
          {
            path: 'client-invitation-manager',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/client-invitation-manager/client-invitation-manager.component').then(
                (c) => c.ClientInvitationManagerComponent,
              ),
          },
          {
            path: 'lead-clients',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/all-clients-list/all-clients-list.component').then(
                (c) => c.AllClientsListComponent,
              ),
          },
          {
            path: 'detail/:uid',
            loadComponent: () =>
              import('./pages/main-pages/clients-overview/client-details/client-details.component').then(
                (c) => c.ClientDetailsComponent,
              ),
            children: [
              { path: '', redirectTo: 'info', pathMatch: 'full' },
              {
                path: 'info',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-info/client-info.component').then(
                    (c) => c.ClientInfoComponent,
                  ),
              },
              {
                path: 'finance-check-edit',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-info/finance-check-edit/finance-check-edit.component').then(
                    (c) => c.FinanceCheckEditComponent,
                  ),
              },
              {
                path: 'documents',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-documents/client-documents.component').then(
                    (c) => c.ClientDocumentsComponent,
                  ),
              },
              {
                path: 'depot',
                canActivate: [restrictedFeatureGuard('depots')],
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-depot/client-depot.component').then(
                    (c) => c.ClientDepotComponent,
                  ),
              },
              {
                path: 'follow-ups',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-follow-ups/client-follow-ups.component').then(
                    (m) => m.ClientFollowUpsComponent,
                  ),
              },
              {
                path: 'contracts',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-contracts/client-contracts.component').then(
                    (c) => c.ClientContractsComponent,
                  ),
              },
              {
                path: 'contracts/contract',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-contracts/client-contract-detail/client-contract-detail.component').then(
                    (c) => c.ClientContractDetailComponent,
                  ),
              },
              {
                path: 'contracts/contract/:uid',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-contracts/client-contract-detail/client-contract-detail.component').then(
                    (c) => c.ClientContractDetailComponent,
                  ),
              },
              {
                path: 'contract-documents-overview',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-contract-documents-overview/client-contract-documents-overview.component').then(
                    (c) => c.ClientContractDocumentsOverviewComponent,
                  ),
              },
              {
                path: 'analyse',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/contract-ai-analysis/contract-ai-analysis.component').then(
                    (c) => c.ContractAiAnalysisComponent,
                  ),
              },
              {
                path: 'cashflow',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-cashflow-analysis/client-cashflow-analysis.component').then(
                    (c) => c.ClientCashflowAnalysisComponent,
                  ),
              },
              {
                path: 'bank-account-overview',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/client-bank-account-overview.component').then(
                    (c) => c.ClientBankAccountOverviewComponent,
                  ),
              },
              {
                path: 'bank-account-overview/bank-account-create-analyse',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/bank-account-analyse-creation/bank-account-analyse-creation.component').then(
                    (c) => c.BankAccountAnalyseCreationComponent,
                  ),
              },
              {
                path: 'bank-account-overview/bank-account-create-analyse/:id',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/bank-account-analyse-creation/bank-account-analyse-creation.component').then(
                    (c) => c.BankAccountAnalyseCreationComponent,
                  ),
              },
              {
                path: 'bank-account-overview/bank-account-analyse/:id',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/bank-account-analyse/bank-account-analyse.component').then(
                    (c) => c.BankAccountAnalyseComponent,
                  ),
              },
              {
                path: 'bank-account-overview/bank-account-analyse/:id/analyse-overview',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/bank-account-analyse/bank-account-analyse.component').then(
                    (c) => c.BankAccountAnalyseComponent,
                  ),
              },
              {
                path: 'bank-account-overview/depot-account-details/:id',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-bank-account-overview/depot-account-details/depot-account-details.component').then(
                    (c) => c.DepotAccountDetailsComponent,
                  ),
              },
              {
                path: 'insign',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-insign/client-insign.component').then(
                    (c) => c.ClientInsignComponent,
                  ),
              },
              {
                path: 'wealth',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-wealth/client-wealth.component').then(
                    (c) => c.ClientWealthComponent,
                  ),
              },
              {
                path: 'wealth/asset',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-wealth/wealth-asset-creation/wealth-asset-creation.component').then(
                    (c) => c.WealthAssetCreationComponent,
                  ),
              },
              {
                path: 'wealth/details/:category',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-wealth/wealth-asset-details/wealth-asset-details.component').then(
                    (c) => c.WealthAssetDetailsComponent,
                  ),
              },
              {
                path: 'wealth/:category/:assetsId/:uuid',
                loadComponent: () =>
                  import('./pages/main-pages/clients-overview/client-details/client-wealth/wealth-asset-details/wealth-asset-details.component').then(
                    (c) => c.WealthAssetDetailsComponent,
                  ),
              },
              {
                path: 'ai-chat',
                loadComponent: () =>
                  import('./shared/components/ai-chat/ai-chat.component').then((c) => c.AiChatComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'follow-ups-overview',
        canActivate: [restrictedFeatureGuard('follow-ups')],
        loadComponent: () =>
          import('./pages/main-pages/follow-ups-overview/follow-ups-overview.component').then(
            (c) => c.FollowUpsOverviewComponent,
          ),
      },
      {
        path: 'depot-overview',
        canActivate: [restrictedFeatureGuard('depots')],
        loadComponent: () =>
          import('./pages/main-pages/depot-overview/depot-overview.component').then((c) => c.DepotOverviewComponent),
      },
      {
        path: 'client-invitation-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/main-pages/client-invitation-overview/client-invitation-overview.component').then(
            (c) => c.ClientInvitationOverviewComponent,
          ),
      },
      {
        path: 'contracts-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/main-pages/contracts-overview/contracts-overview.component').then(
            (c) => c.ContractsOverviewComponent,
          ),
      },
      {
        path: 'vinhub-overview',
        canActivate: [restrictedFeatureGuard('chats')],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/main-pages/vinhub-overview/vinhub-overview.component').then((c) => c.VinhubOverviewComponent),
        children: [
          { path: '', redirectTo: 'all-messages', pathMatch: 'full' },
          {
            path: 'all-messages',
            loadComponent: () =>
              import('./pages/main-pages/vinhub-overview/messages-tab/messages-tab.component').then(
                (c) => c.MessagesTabComponent,
              ),
          },
          {
            path: 'unread-messages',
            loadComponent: () =>
              import('./pages/main-pages/vinhub-overview/messages-tab/messages-tab.component').then(
                (c) => c.MessagesTabComponent,
              ),
          },
          {
            path: ':uid/vinhub',
            loadChildren: () =>
              import('./pages/main-pages/vinhub-overview/vinhub/vinhub.module').then((c) => c.VinhubModule),
          },
        ],
      },
      // Analyse Pages
      {
        path: 'activity',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () => import('./pages/main-pages/activity/activity.component').then((c) => c.ActivityComponent),
        children: [
          {
            path: '',
            redirectTo: 'activity-overview',
            pathMatch: 'full',
          },
          {
            path: 'activity-overview',
            loadComponent: () =>
              import('./pages/main-pages/activity/activity-overview/activity-overview.component').then(
                (c) => c.ActivityOverviewComponent,
              ),
          },
          {
            path: 'activity-manager',
            loadComponent: () =>
              import('./pages/main-pages/activity/activity-manager/activity-manager.component').then(
                (c) => c.ActivityManagerComponent,
              ),
          },
        ],
      },
      {
        path: 'coverage-analyse-overview',
        loadComponent: () =>
          import('./pages/analyse-pages/covarage-analyse-overview/coverage-analyse-overview.component').then(
            (c) => c.CoverageAnalyseOverviewComponent,
          ),
      },
      {
        path: 'cashflow-analyse-overview',
        canActivate: [restrictedFeatureGuard('cashflow-analyse')],
        loadComponent: () =>
          import('./pages/analyse-pages/cashflow-analyse-overview/cashflow-analyse-overview.component').then(
            (c) => c.CashflowAnalyseOverviewComponent,
          ),
      },
      {
        path: 'person-profile-analyse',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/analyse-pages/person-profile-analyse-overview/person-profile-analyse-overview.component').then(
            (c) => c.PersonProfileAnalyseOverviewComponent,
          ),
      },
      {
        path: 'portfolio-analyse',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/analyse-pages/portfolio-analyse-overview/portfolio-analyse-overview.component').then(
            (c) => c.PortfolioAnalyseOverviewComponent,
          ),
      },
      // Funktionskatalog with guided tours
      {
        path: 'feature-catalog',
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/feature-catalog/feature-catalog.component').then((c) => c.FeatureCatalogComponent),
      },
      // Profile Pages
      {
        path: 'profile',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/profile-pages/profile-pages.component').then((c) => c.ProfilePagesComponent),

        children: [
          {
            path: '',
            redirectTo: 'settings',
            pathMatch: 'full',
          },
          {
            path: 'settings',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/settings/settings.component').then((c) => c.SettingsComponent),
          },
          {
            path: 'absences',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/absences/absences.component').then((c) => c.AbsencesComponent),
          },
          {
            path: 'profile-tab',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/profile-tab/profile-tab.component').then((c) => c.ProfileTabComponent),
            children: [
              { path: '', redirectTo: 'company', pathMatch: 'full' },
              {
                path: 'company',
                canActivate: [],
                data: { roles: [] },
                loadComponent: () =>
                  import('./pages/profile-pages/profile-tab/company-data/company-data.component').then(
                    (c) => c.CompanyDataComponent,
                  ),
              },
              {
                path: 'consent-initial-information',
                canActivate: [],
                data: { roles: [] },
                loadComponent: () =>
                  import('./pages/profile-pages/profile-tab/consent-initial-information/consent-initial-information.component').then(
                    (c) => c.ConsentInitialInformationComponent,
                  ),
              },
              {
                path: 'consent-privacy-client',
                canActivate: [],
                data: { roles: [] },
                loadComponent: () =>
                  import('./pages/profile-pages/profile-tab/consent-privacy-client/consent-privacy-client.component').then(
                    (c) => c.ConsentPrivacyClientComponent,
                  ),
              },
            ],
          },
          {
            path: 'customer-app',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/customer-app-data/customer-app-data.component').then(
                (c) => c.CustomerAppDataComponent,
              ),
          },
          {
            path: 'structure',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/company-structure/company-structure.component').then(
                (c) => c.CompanyStructureComponent,
              ),
          },
          {
            path: 'structure/features',
            canActivate: [structureFeaturesGuard],
            loadComponent: () =>
              import('./pages/profile-pages/company-structure/company-structure-features/company-structure-features.component').then(
                (c) => c.CompanyStructureFeaturesComponent,
              ),
          },
          {
            path: 'structure/:companyId',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/company-structure/company-structure-company/company-structure-company.component').then(
                (c) => c.CompanyStructureCompanyComponent,
              ),
          },
          {
            path: 'structure/:companyId/advisor/:advisorId',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/company-structure/company-structure-advisor/company-structure-advisor.component').then(
                (c) => c.CompanyStructureAdvisorComponent,
              ),
          },
          {
            path: 'mandate',
            loadComponent: () =>
              import('./pages/profile-pages/mandate-data/mandate-data.component').then((c) => c.MandateDataComponent),
            canActivate: [],
            data: { roles: [] },
            children: [
              { path: '', redirectTo: 'mandate-form', pathMatch: 'full' },
              {
                path: 'mandate-form',
                loadComponent: () =>
                  import('./pages/profile-pages/mandate-data/mandate-form/mandate-form.component').then(
                    (c) => c.MandateFormComponent,
                  ),
              },
              {
                path: 'power-attorney-form',
                loadComponent: () =>
                  import('./pages/profile-pages/mandate-data/power-of-attorney-form/power-of-attorney-form.component').then(
                    (c) => c.PowerOfAttorneyFormComponent,
                  ),
              },
              {
                path: 'privacy-consent-form',
                loadComponent: () =>
                  import('./pages/profile-pages/mandate-data/privacy-consent/privacy-consent.component').then(
                    (c) => c.PrivacyConsentComponent,
                  ),
              },
              // {
              //   path: 'signature-form',
              //   loadComponent: () =>
              //     import('./pages/profile/mandate-data/signature-form/signature-form.component').then(
              //       (c) => c.SignatureFormComponent,
              //     ),
              // },
            ],
          },
          {
            path: 'links-manager',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/links-manager/links-manager.component').then(
                (c) => c.LinksManagerComponent,
              ),
          },
          {
            path: 'pools',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/pool-connection/pool-connection.component').then(
                (c) => c.PoolConnectionComponent,
              ),
          },
          // {
          //   path: 'data-import-table',
          //   loadComponent: () =>
          //     import('./pages/profile/data-importer/data-importer.component').then((c) => c.DataImporterComponent),
          //   canActivate: [ ],
          //   data: { roles: [Roles.DATA_IMPORTER] },
          //   children: [
          //     {
          //       path: '',
          //       redirectTo: 'data-import-table',
          //       pathMatch: 'full',
          //     },
          //     {
          //       path: 'clients-import',
          //       loadComponent: () =>
          //         import('./pages/profile/data-importer/clients-import/clients-import.component').then(
          //           (c) => c.ClientsImportComponent,
          //         ),
          //     },
          //   ],
          // },
          {
            path: 'subscription-manager',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/subscription-manager/subscription-manager.component').then(
                (c) => c.SubscriptionManagerComponent,
              ),
            children: [
              {
                path: '',
                redirectTo: 'abonament-manager',
                pathMatch: 'full',
              },
              {
                path: 'abonament-manager',
                loadComponent: () =>
                  import('./pages/profile-pages/subscription-manager/abonament-manager/abonament-manager.component').then(
                    (c) => c.AbonamentManagerComponent,
                  ),
              },
              {
                path: 'payment-manager',
                loadComponent: () =>
                  import('./pages/profile-pages/subscription-manager/payment-manager/payment-manager.component').then(
                    (c) => c.PaymentManagerComponent,
                  ),
              },
              {
                path: 'payment-invoices-overview',
                loadComponent: () =>
                  import('./pages/profile-pages/subscription-manager/payment-invoices-overview/payment-invoices-overview.component').then(
                    (c) => c.PaymentInvoicesOverviewComponent,
                  ),
              },
            ],
          },
          {
            path: 'advisor-create',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/subscription-advisor-edit/subscription-advisor-edit.component').then(
                (c) => c.SubscriptionAdvisorEditComponent,
              ),
          },
          {
            path: 'subscription-advisor-edit/:id',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/subscription-advisor-edit/subscription-advisor-edit.component').then(
                (c) => c.SubscriptionAdvisorEditComponent,
              ),
          },
          {
            path: 'subscription-payment-error',
            canActivate: [],
            data: { roles: [] },
            loadComponent: () =>
              import('./pages/profile-pages/subscription-payment-error/subscription-payment-error.component').then(
                (c) => c.SubscriptionPaymentErrorComponent,
              ),
          },
        ],
      },
      {
        path: 'sync-report/:jobId',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/profile-pages/pool-connection/sync-report/sync-report.component').then(
            (c) => c.SyncReportComponent,
          ),
      },
      // Utils Pages
      {
        path: 'contracts-termination-overview',
        canActivate: [restrictedFeatureGuard('contracts-termination')],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/contracts-termination-overview/contracts-termination-overview.component').then(
            (c) => c.ContractsTerminationOverviewComponent,
          ),
      },
      {
        path: 'contract-termination-creation/:id',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/contracts-termination-overview/contract-termination-creation/contract-termination-creation.component').then(
            (c) => c.ContractTerminationCreationComponent,
          ),
      },
      {
        path: 'contracts-transfer-overview',
        canActivate: [restrictedFeatureGuard('contracts-transfer')],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/contracts-transfer-overview/contracts-transfer-overview.component').then(
            (c) => c.ContractsTransferOverviewComponent,
          ),
      },
      {
        path: 'contract-transfer-creation/:id',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/contracts-transfer-overview/contract-transfer-creation/contract-transfer-creation.component').then(
            (c) => c.ContractTransferCreationComponent,
          ),
      },
      {
        path: 'media-studio',
        canActivate: [restrictedFeatureGuard('media-studio')],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/media-studio/media-studio.component').then((c) => c.MediaStudioComponent),
        children: [
          {
            path: '',
            redirectTo: 'video-library',
            pathMatch: 'full',
          },
          {
            path: 'video-library',
            loadComponent: () =>
              import('./pages/utils-pages/media-studio/video-library/video-library.component').then(
                (c) => c.VideoLibraryComponent,
              ),
          },
          // {
          //   path: 'audio-library',
          //   loadComponent: () =>
          //     import('./pages/media-studio/audio-library/audio-library.component').then((c) => c.AudioLibraryComponent),
          // },
          {
            path: 'video-creation',
            loadComponent: () =>
              import('./pages/utils-pages/media-studio/video-library/video-creation/video-creation.component').then(
                (c) => c.VideoCreationComponent,
              ),
            children: [
              {
                path: 'upload',
                loadComponent: () =>
                  import('./pages/utils-pages/media-studio/video-library/video-creation/video-creation.component').then(
                    (c) => c.VideoCreationComponent,
                  ),
              },
            ],
          },
          {
            path: 'video-edit/:id',
            loadComponent: () =>
              import('./pages/utils-pages/media-studio/video-library/video-edit/video-edit.component').then(
                (c) => c.VideoEditComponent,
              ),
          },
          // {
          //   path: 'audio-creation',
          //   loadComponent: () =>
          //     import('./pages/media-studio/audio-library/audio-creation/audio-creation.component').then(
          //       (c) => c.AudioCreationComponent,
          //     ),
          // },
          // {
          //   path: 'audio-edit',
          //   loadComponent: () =>
          //     import('./pages/media-studio/audio-library/audio-edit/audio-edit.component').then(
          //       (c) => c.AudioEditComponent,
          //     ),
          // },
        ],
      },
      {
        path: 'insign-overview',
        canActivate: [restrictedFeatureGuard('insign')],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/insign-overview/insign-overview.component').then(
            (c) => c.InsignOverviewComponent,
          ),
      },
      {
        path: 'integration-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/integration-overview/integration-overview.component').then(
            (c) => c.IntegrationOverviewComponent,
          ),
      },
      {
        path: 'broker-mandate-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/broker-mandate-overview/broker-mandate-overview.component').then(
            (c) => c.BrokerMandateOverviewComponent,
          ),
      },
      {
        path: 'marketing-overview',
        canActivate: [],
        data: { roles: [] },
        loadComponent: () =>
          import('./pages/utils-pages/marketing-overview/marketing-overview.component').then(
            (c) => c.MarketingOverviewComponent,
          ),
      },
      // Restricted Pages
      {
        path: 'restricted-product/:feature',
        loadComponent: () =>
          import('./pages/restricted-page/restricted-page.component').then((c) => c.RestrictedPageComponent),
      },
    ],
  },
  // TODO: add ErrorPage for handle 400/500 errors
  { path: '**', redirectTo: '' },
];

const routerConfig: ExtraOptions = {
  useHash: false,
  enableTracing: false,
  scrollPositionRestoration: 'top',
};

@NgModule({
  imports: [RouterModule.forRoot(staticRoutes, routerConfig)],
  providers: [],
  exports: [RouterModule],
})
export class AppRoutesModule {}
